import saveAs from 'file-saver';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ReactComponent as Close2 } from '../../../assets/img/storage/actions-close.svg';
import { ReactComponent as Copy } from '../../../assets/img/storage/actions-copy.svg';
import { ReactComponent as Download } from '../../../assets/img/storage/actions-dl.svg';
import { ReactComponent as Close } from '../../../assets/svg/Close28.svg';
import { ReactComponent as Extract } from '../../../assets/svg/ExtractAndStay.svg';
import { useAlert } from '../../../components/alert';
import { extractErrorDetail, formatBytes, toMplatDate } from '../../../components/common';
import Button from '../../../components/mplat/Button/Button';
import { BaseModalProps, Modal } from '../../../components/mplat/Modal';
import MultiSelect from '../../../components/mplat/MultiSelect';
import NumberInput from '../../../components/mplat/NumberInput';
import Select from '../../../components/mplat/Select';
import TextInput from '../../../components/mplat/TextInput';
import { extractPrefix } from '../../../pages/ContentEditorPage';
import { SdnLinkType, SdnMeta, SdnOperationMessageActionRequired, SdnOperationMessageParamsListZip, useCreateSdnLinkMutation, useDeleteSdnLinkMutation, useGetSdnConfigQuery, useGetTemporalAccessKeyMutation, useLazyGetSdnLinksQuery, useLazyGetSdnReportQuery, useReplyToSdnOperationMutation } from '../../../services/sdn';
import { createPreSignUrl } from '../services/operations';
import { createS3ClientWithMD5 } from './md5ClientS3';

interface StorageShareModalProps extends BaseModalProps {
    prefix: string,
    bucketname: string,
}

export function StorageShareModal({ isOpen, onClose, bucketname, prefix }: StorageShareModalProps) {
    const [url, setUrl] = useState('');

    const [getTempKey] = useGetTemporalAccessKeyMutation();
    const { data: s3config, error: s3Error } = useGetSdnConfigQuery();

    useEffect(() => {
        if (isOpen) {
            setUrl('');
        }
    }, [isOpen]);

    return (
        <Modal className="popup_storage popup_storage-share" isOpen={isOpen} onClose={onClose}>
            <form action="#" className="form storage__popup storage__popup-share">
                <div className="storage__popup-heading">
                    <Trans>storage:FILE_LINK</Trans>
                </div>
                <div className="storage__popup-copy-content">
                    {s3Error && extractErrorDetail(s3Error)}
                </div>
                <div className="storage__popup-cta">
                    <Button className={url ? '_copied' : ''} onClick={() => {
                        if (url) {
                            navigator.clipboard.writeText(url);
                        } else {
                            getTempKey().unwrap().then((v) => {
                                const access = v.results;
                                const client = s3config?.results.enabled && createS3ClientWithMD5({
                                    region: 'EU',
                                    endpoint: s3config?.results.s3api,
                                    forcePathStyle: s3config.results['access-type'] === 'path-style',
                                    credentials: {
                                        accessKeyId: access?.accesskey, //'t2nEhbS2aUbLXDjktmQs',
                                        secretAccessKey: access?.secretkey, // '1nVdAs2qUIPoXUIE6ukPuwLLUMsbtk1ptDlblebD',
                                        sessionToken: access?.token,
                                    },
                                    requestChecksumCalculation: 'WHEN_REQUIRED',
                                    apiVersion: 's3v4',
                                });
                                client && createPreSignUrl(client, bucketname, prefix).then((link) => {
                                    setUrl(link);
                                    navigator.clipboard.writeText(link);
                                }).catch((e) => console.error(e))
                            })
                        }
                    }}>{url ? t('Copied') : t('Copy')}</Button>
                </div>
            </form>
        </Modal>
    )
}

interface StorageLinkModalProps extends BaseModalProps {
    prefix: string,
    bucketname: string,
}

export function StorageLinkModal({ isOpen, onClose, bucketname, prefix }: StorageLinkModalProps) {
    const alert = useAlert();

    const [expire, setExpire] = useState(86400);
    const [comment, setComment] = useState('');
    // const [copied, setCopied] = useState(-1);
    const [alimit, setALimit] = useState(10);
    const [subjects, setSubjects] = useState<string[]>([]);
    const [availSubjects, setAvailSubjects] = useState<{ value: string, label: string }[]>([]);

    const [fetchSdnLinks, { data: links, error }] = useLazyGetSdnLinksQuery();
    const [createSdnLink] = useCreateSdnLinkMutation();
    const [deleteSdnLink] = useDeleteSdnLinkMutation();

    const expires = [
        { value: 86400, label: `1 ${t('day')}` },
        { value: 2 * 86400, label: `2 ${t('days2')}` },
        { value: 3 * 86400, label: `3 ${t('days2')}` },
        { value: 4 * 86400, label: `4 ${t('days2')}` },
        { value: 5 * 86400, label: `5 ${t('days')}` },
        { value: 6 * 86400, label: `6 ${t('days')}` },
        { value: 7 * 86400, label: `7 ${t('days')}` },
    ];

    useEffect(() => {
        if (isOpen) {
            fetchSdnLinks();
            // setCreatedLinks([]);
            setAvailSubjects([]);
            setSubjects([]);
            setComment('');
        }
    }, [isOpen]);

    return (
        <Modal className="popup_storage popup_storage-share" isOpen={isOpen} onClose={onClose}>
            <form action="#" className="form storage__popup storage__popup-share">
                <div className="storage__popup-heading">
                    <Trans>storage:FILE_LINK</Trans>
                </div>
                <div className="storage__popup-copy-content">
                    {error && extractErrorDetail(error)}
                    {links?.results.filter((link) => link.prefixes.some((prfx) => extractPrefix(prfx) === prefix)).map((link) =>
                        <React.Fragment key={link.id}>
                            <br />
                            <div className="storage__popup-subheading">
                                <Trans>storage:LINK_EXPIRATION</Trans> <Trans>for</Trans> {link.subjects.join(', ')}
                                <br />
                            </div>
                            <div className="storage__popup-item storage__popup-share-start">
                                <div className="storage__popup-item-label">
                                    <Trans>storage:LINK_PERIOD_START</Trans>
                                </div>
                                <div className="storage__popup-share-date">
                                    {toMplatDate(link.added)}
                                </div>
                            </div>
                            <div className="storage__popup-item storage__popup-share-end">
                                <div className="storage__popup-item-label">
                                    <Trans>storage:LINK_PERIOD_END</Trans>
                                </div>
                                <div className="storage__popup-share-end-when" />
                                <div className="storage__popup-share-date _show">
                                    {toMplatDate(link.expire)}
                                </div>
                            </div>
                            "{link.comment || link.id}" <Trans>storage:ALIMIT</Trans>: {link.alimit}
                            <div className="storage__popup-cta">
                                {/* {<Button
                                    className={link.id === copied ? '_copied' : ''}
                                    onClick={() => {
                                        navigator.clipboard.writeText(link.location);
                                        setCopied(link.id);
                                        setTimeout(() => setCopied(-1), 1000);
                                    }}
                                >
                                    {link.id === copied ? t('Copied') : t('Copy')}
                                </Button>} */}
                                <button
                                    type="button"
                                    className="storage__popup-share-remove"
                                    onClick={() => deleteSdnLink(link.id.toString())}
                                >
                                    <Trans>Delete</Trans>
                                </button>
                            </div>
                        </React.Fragment>
                    )}
                    <hr />
                    <>
                        <div className="storage__popup-subheading">
                            <Trans>storage:LINK_EXPIRATION</Trans>
                        </div>
                        <Select value={expire} options={expires} onChange={(v) => setExpire(v)} />
                        <div className="storage__popup-subheading">
                            <Trans>storage:ALIMIT</Trans>
                        </div>
                        <NumberInput value={alimit} setValue={(v) => setALimit(v)} min={1} max={100} placeholder={t('storage:ALIMIT')} />
                        <MultiSelect
                            value={subjects}
                            setValue={(v) => setSubjects(v)}
                            editable
                            placeholder={t('storage:EMAILS')}
                            createHeader={t('storage:EMAIL_ADD')}
                            createPlaceholder={t('Email')}
                            onCreate={(v) => {
                                const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
                                if (regex.test(v)) {
                                    setAvailSubjects((p) => [...p, { value: v, label: v }])
                                    setSubjects((p) => [...p, v]);
                                    return true;
                                }
                                return false;
                            }}
                            options={availSubjects}
                        />
                        <br />
                        <TextInput value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t('Comment')} />
                        {<Button
                            className={''}
                            onClick={() => {
                                createSdnLink({
                                    type: SdnLinkType.PUBLIC, //prefix.endsWith('/') ? SdnLinkType.PUBLIC : SdnLinkType.PRESIGNED,
                                    prefixes: [`${bucketname}/${prefix}`],
                                    comment: comment,
                                    expire_in: expire,
                                    subjects: subjects,
                                    alimit: alimit,
                                }).unwrap().then((v) => {
                                    console.log(v);
                                }).catch((e) => alert.error(extractErrorDetail(e)))
                            }}
                        >
                            <Trans>Send</Trans>
                        </Button>}
                    </>
                </div>
            </form>
        </Modal>
    )
}

interface StorageListZipModalProps extends BaseModalProps {
    listzip: SdnOperationMessageParamsListZip | null,
    onExtract?: (s: string[]) => void
}

export function StorageListZipModal({ listzip, onClose, onExtract, ...props }: StorageListZipModalProps) {
    const [selected, setSelected] = useState<string[]>([]);

    if (!listzip) return null;

    return ReactDOM.createPortal(
        <div className="storage__viewer _show" style={{ opacity: 1 }} {...props} >
            <div className="storage__viewer-close" title={t('Close')} onClick={onClose}>
                <Close />
            </div>
            <div className="storage__viewer-main" style={{ transform: 'scale(1)' }}>
                <div className="swiper-wrapper">
                    <div className="swiper-slide swiper-slide-duplicate swiper-slide-active">
                        <div className="storage__viewer-item">
                            <div className="storage__viewer-archive">
                                <div className="storage__viewer-archive-name">{listzip?.source}</div>
                                <div className="storage__viewer-archive-info">{formatBytes(listzip?.listing.reduce((acc, curr) => acc + curr.compressed, 0) || 0)} · {listzip?.listing.length} файлов</div>
                                <div className="storage__viewer-archive-list _active">
                                    {/* <div className="storage__viewer-archive-list-back" data-id="0"><svg width="21" height="16" fill="none" viewBox="0 0 21 16"><path stroke="#323657" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 8H1m0 0 7-7M1 8l7 7"></path></svg>Назад</div> */}
                                    {/* <div className="storage__viewer-archive-list-item _folder" data-id="1">Document fonts</div> */}
                                    {[...listzip?.listing || []].sort((a, b) => a.filename > b.filename ? 1 : (a.filename < b.filename ? -1 : 0)).map((file) => (
                                        <a
                                            key={file.filename}
                                            href="#"
                                            download=""
                                            className={`storage__viewer-archive-list-item _file ${selected.includes(file.filename) ? '_selected' : ''}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setSelected((p) => p.includes(file.filename) ? p.filter((f) => f !== file.filename) : [...p, file.filename]);
                                            }}
                                            style={{ background: selected.includes(file.filename) ? 'var(--c-beige)' : '' }}
                                        >
                                            <div className="storage__viewer-archive-list-item-name">{file.filename}</div>
                                            <div className="storage__viewer-archive-list-item-dl">
                                                {formatBytes(file.compressed)}
                                                {/* <DownloadIcon />
                                                <span className="storage__viewer-archive-list-item-dl-label">
                                                    <Trans>Download</Trans>
                                                </span> */}
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {selected.length > 0 && <div className="storage__file-actions storage__viewer-actions _archive">
                <div className="storage__file-actions-group _main">
                </div>
                <div className="storage__file-actions-group _storage">
                    {onExtract && <div className="storage__file-actions-item _unpack-save" onClick={() => onExtract(selected)}>
                        <Extract />
                        <span className="storage__file-actions-item-label">
                            <Trans>storage:EXTRACT</Trans>
                        </span>
                    </div>}
                    <div className="storage__file-actions-item _close" onClick={() => setSelected([])}>
                        <Close2 />
                        <span className="storage__file-actions-item-label">
                            <Trans>Close</Trans>
                        </span>
                    </div>
                </div>
            </div>
            }
        </div>
        , document.getElementsByClassName('main')[0]);
}

interface StorageVideoModalProps extends BaseModalProps {
    url: string | null,
    name: string,
}

export function StorageVideoModal({ url, name, isOpen, onClose, ...props }: StorageVideoModalProps) {
    if (!isOpen) return null;
    if (!url) return null;

    let ext = name.slice(name.lastIndexOf('.') + 1);
    if (ext === 'mkv') {
        ext = 'mp4';
    }

    return ReactDOM.createPortal(
        <div className="storage__viewer _show" style={{ opacity: 1 }} {...props} >
            <div className="storage__viewer-close" title={t('Close')} onClick={onClose}>
                <Close />
            </div>
            <div className="storage__viewer-main" style={{ transform: 'scale(1)' }}>
                <div className="swiper-wrapper">
                    <div className="swiper-slide swiper-slide-duplicate swiper-slide-active">
                        <div className="storage__viewer-item">
                            <div className="storage__viewer-media">
                                <div className="storage__viewer-media-item">
                                    <video controls preload="auto" playsInline onError={(e) => console.log(e)}>
                                        <source type={`video/${ext}`} src={url} />
                                    </video>
                                </div>
                                <div className="storage__viewer-media-name">
                                    {name}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="storage__file-actions storage__viewer-actions _archive">
                <div className="storage__file-actions-group _main">
                    {url && <div className="storage__file-actions-item _download" onClick={() => {
                        const a = document.createElement('a');
                        a.href = url;
                        a.target = '_blank';
                        a.download = 'file'; // Use the original filename
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url); // Clean up the temporary URL
                    }}>
                        <Download />
                        <span className="storage__file-actions-item-label">
                            <Trans>Download</Trans>
                        </span>
                    </div>}
                </div>
                <div className="storage__file-actions-group _storage">
                    <div className="storage__file-actions-item _close" onClick={onClose}>
                        <Close2 />
                        <span className="storage__file-actions-item-label">
                            <Trans>Close</Trans>
                        </span>
                    </div>
                </div>
            </div>
        </div>
        , document.getElementsByClassName('main')[0]);
}

interface StorageReplaceFileModalProps extends BaseModalProps {
    files: File[],
    onReplace: () => void,
    onSaveBoth: () => void,
}

export function StorageReplaceFileModal({ files, isOpen, onClose, onReplace, onSaveBoth }: StorageReplaceFileModalProps) {
    return (
        <Modal className="popup_storage popup_storage-folder" isOpen={isOpen} onClose={onClose}>
            <div className="storage__popup storage__popup-message">
                <div className="storage__popup-heading _error">
                    <Trans>storage:REPLACE_FILE</Trans>
                </div>
                <div className="storage__popup-copy-content">
                    <p>
                        {files.length > 1 ?
                            t('storage:REPLACE_FILE_MESSAGES_1', { value: files.length.toString() })
                            :
                            t('storage:REPLACE_FILE_MESSAGE_1', { value: `${files[0]?.name}` })
                        }
                    </p>
                    <p>
                        <Trans>storage:REPLACE_FILE_MESSAGE_2</Trans>
                    </p>
                </div>
                <div className="storage__popup-cta">
                    <Button onClick={() => { onReplace(); onClose(); }}>
                        <Trans>storage:REPLACE</Trans>
                    </Button>
                    <button type="button" className="storage__popup-cta-link-btn" onClick={() => { onSaveBoth(); onClose(); }}>
                        <Trans>storage:SAVE_BOTH</Trans>
                    </button>
                </div>
            </div>
        </Modal>
    )
}

export function StorageMemoryLimitModal({ isOpen, onClose }: BaseModalProps) {

    return (
        <Modal className="popup_storage popup_storage-folder" isOpen={isOpen} onClose={onClose}>
            <div className="storage__popup storage__popup-message">
                <div className="storage__popup-heading _error">
                    <Trans>storage:UPLOAD_LIMIT</Trans>
                </div>
                <div className="storage__popup-copy-content">
                    <Trans>storage:UPLOAD_LIMIT_MESSAGE</Trans>
                </div>
                <div className="storage__popup-cta">
                    <Link to="/tariff" className="btn">
                        <Trans>storage:TARIFF_EXPAND</Trans>
                    </Link>
                    <button type="button" className="storage__popup-cta-link-btn" onClick={onClose}>
                        <Trans>Close</Trans>
                    </button>
                </div>
            </div>
        </Modal>
    )
}

export function StorageMemoryLimitInterruptModal({ isOpen, onClose }: BaseModalProps) {

    return (
        <Modal className="popup_storage popup_storage-folder" isOpen={isOpen} onClose={onClose}>
            <div className="storage__popup storage__popup-message">
                <div className="storage__popup-heading _error">
                    <Trans>storage:UPLOAD_INTERRUPTED</Trans>
                </div>
                <div className="storage__popup-copy-content">
                    <Trans>storage:UPLOAD_INTERRUPTED_MESSAGE</Trans>
                </div>
                <div className="storage__popup-cta">
                    <Link to="/tariff" className="btn">
                        <Trans>storage:TARIFF_EXPAND</Trans>
                    </Link>
                    <button type="button" className="storage__popup-cta-link-btn" onClick={onClose}>
                        <Trans>Close</Trans>
                    </button>
                </div>
            </div>
        </Modal>
    )
}

interface StorageDeleteConfirmModalProps extends BaseModalProps {
    files: string[],
    onDelete: () => void,
    mbDisclamer?: boolean,
    mbAccessDisclamer?: boolean,
    mbShareDisclamer?: boolean,
}

export function StorageDeleteConfirmModal({ isOpen, files, mbDisclamer, mbAccessDisclamer, mbShareDisclamer, onDelete, onClose }: StorageDeleteConfirmModalProps) {

    return (
        <Modal className="popup_storage popup_storage-folder" isOpen={isOpen} onClose={onClose}>
            <div className="storage__popup storage__popup-message">
                <div className="storage__popup-heading _error">
                    <Trans>storage:DELETE</Trans>
                </div>
                <div className="storage__popup-copy-content">
                    {mbDisclamer && <strong>
                        <Trans>storage:DELETE_LINKED_DISCLAMER</Trans>
                        <br />
                        <br />
                    </strong>}
                    {mbAccessDisclamer && mbShareDisclamer && <strong>
                        <Trans>storage:DELETE_ACCESSED_SHARED_DISCLAMER</Trans>
                        <br />
                        <br />
                    </strong>}
                    {mbAccessDisclamer && !mbShareDisclamer && <strong>
                        <Trans>storage:DELETE_ACCESSED_DISCLAMER</Trans>
                        <br />
                        <br />
                    </strong>}
                    {mbShareDisclamer && !mbAccessDisclamer && <strong>
                        <Trans>storage:DELETE_SHARED_DISCLAMER</Trans>
                        <br />
                        <br />
                    </strong>}
                    {files.length === 1 ?
                        files[0]
                        :
                        `${t('Delete')} ${files.length} ${t('files')}?`
                    }
                </div>
                <div className="storage__popup-cta">
                    <Button onClick={() => onDelete()}>
                        <Trans>Delete</Trans>
                    </Button>
                    <button type="button" className="storage__popup-cta-link-btn" onClick={onClose}>
                        <Trans>Close</Trans>
                    </button>
                </div>
            </div>
        </Modal>
    )
}

interface StorageReplyModalProps extends BaseModalProps {
    needReply: SdnOperationMessageActionRequired & { opId: string }
    onSuccess?: () => void,
}

export function StorageReplyModal({ isOpen, onClose, onSuccess, needReply, }: StorageReplyModalProps) {
    const alert = useAlert();
    const [replyOperation] = useReplyToSdnOperationMutation();

    const [needReplyChoose, setNeedRelpyChoose] = useState<string>(needReply.action);

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <div className="storage__popup storage__popup-message">
                <div className="storage__popup-heading _error">
                    {needReply.msg}
                    <br />
                    {needReply.params?.target}
                </div>
                <div className="storage__popup-copy-content">
                    <Select value={needReplyChoose} onChange={(v) => setNeedRelpyChoose(v)} options={needReply.actions.map((a) => ({ value: a.id, label: a.name }))} />
                </div>
                <div className="storage__popup-cta">
                    <Button onClick={() => {
                        replyOperation({ action: needReplyChoose, id: needReply.opId, replyto: needReply?.id }).unwrap()
                            .then(() => {
                                onSuccess && onSuccess();
                                onClose();
                            })
                            .catch((e) => alert.error(extractErrorDetail(e)));
                    }}>
                        OK
                    </Button>
                </div>
            </div>
        </Modal>
    )
}


interface StorageReportModalProps extends BaseModalProps {
    meta: SdnMeta,
}

export function StorageReportModal({ isOpen, onClose, meta, }: StorageReportModalProps) {
    const alert = useAlert();

    const [fetchPdf] = useLazyGetSdnReportQuery();

    const [copied, setCopied] = useState('');

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="popup_storage">
            <div className="storage__reports-popup">
                <div className="storage__popup-heading">
                    <Trans>storage:REPORT_MODAL_HEAD</Trans>
                </div>
                <div className="storage__reports-popup-list">
                    <div className="storage__reports-popup-list-item">
                        <div className="storage__reports-popup-list-item-name">
                            {meta.filename}
                        </div>
                        {meta.attributes['attr.cksum.md5'] && (
                            <div className="storage__reports-popup-list-item-report storage__key-item _good">
                                <Trans>storage:FILE_CHECKSUM</Trans> MD5&nbsp;<strong>
                                    <Copy
                                        title={t('Copy')}
                                        onClick={() => {
                                            setCopied(meta.attributes['attr.cksum.md5']);
                                            navigator.clipboard.writeText(meta.attributes['attr.cksum.md5']);
                                            setTimeout(() => setCopied(''), 1000);
                                        }}
                                        style={{ marginTop: '0px' }}
                                    />
                                </strong>
                                {copied === meta.attributes['attr.cksum.md5'] ? <span style={{ color: 'var(--c-btn)' }}><Trans>Copied</Trans></span> : ''}
                            </div>
                        )}
                        {meta.attributes['attr.cksum.sha512'] && (
                            <div className="storage__reports-popup-list-item-report storage__key-item _good">
                                <Trans>storage:FILE_CHECKSUM</Trans> SHA512&nbsp;<strong>
                                    <Copy
                                        title={t('Copy')}
                                        onClick={() => {
                                            setCopied(meta.attributes['attr.cksum.sha512']);
                                            navigator.clipboard.writeText(meta.attributes['attr.cksum.sha512']);
                                            setTimeout(() => setCopied(''), 1000);
                                        }}
                                        style={{ marginTop: '0px' }}
                                    />
                                </strong>
                                {copied === meta.attributes['attr.cksum.sha512'] ? <span style={{ color: 'var(--c-btn)' }}><Trans>Copied</Trans></span> : ''}
                            </div>
                        )}
                        {meta.attributes['attr.cksum.errors'] && (
                            <div className="storage__reports-popup-list-item-report _bad">
                                {extractErrorDetail(meta.attributes['attr.cksum.errors'])}
                            </div>
                        )}
                        {meta.attributes['attr.orbox.checked'] && (
                            <div className={`storage__reports-popup-list-item-report ${meta.attributes['attr.orbox.passed'] ? '_good' : '_bad'}`}>
                                <Link to="" onClick={(ev) => {
                                    ev.preventDefault();
                                    fetchPdf({
                                        prefix: encodeURIComponent(meta.prefix),
                                        reportType: 'attr.orbox.report',
                                        created: encodeURIComponent(meta.attributes['attr.orbox.checked_at'] || ''),
                                    }).unwrap().then((data) => {
                                        saveAs(data, `report_${meta.filename}_${meta.attributes['attr.orbox.checked_at']}.pdf`);
                                    }).catch((err) => {
                                        alert.error(extractErrorDetail(err));
                                    });
                                }}>
                                    <Trans>storage:FILE_REPORT_ORBOX</Trans>
                                </Link>
                            </div>
                        )}
                        {meta.attributes['attr.linza.checked'] && (
                            <div className={`storage__reports-popup-list-item-report ${meta.attributes['attr.linza.passed'] ? '_good' : '_bad'}`}>
                                <Link to="" onClick={(ev) => {
                                    ev.preventDefault();
                                    fetchPdf({
                                        prefix: encodeURIComponent(meta.prefix),
                                        reportType: 'attr.linza.report',
                                        created: encodeURIComponent(meta.attributes['attr.linza.checked_at'] || ''),
                                    }).unwrap().then((data) => {
                                        saveAs(data, `report_${meta.filename}_${meta.attributes['attr.linza.checked_at']}.pdf`);
                                    }).catch((err) => {
                                        alert.error(extractErrorDetail(err));
                                    });
                                }}>
                                    <Trans>storage:FILE_REPORT_LINZA</Trans>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    )
}